var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { parsePhoneNumber, getCountryCallingCode, getCountries, AsYouType, getExampleNumber, } from 'libphonenumber-js/min';
import examples from 'libphonenumber-js/examples.mobile.json';
import libPhoneMetadata from 'libphonenumber-js/metadata.min.json';
import { COUNTRY_NAME_BY_CODE } from '../../../app/main/shops/constants';
var NON_DIGIT_REGEX = /\D/g;
var STARTS_WITH_PLUS_AND_DIGITS_REGEX = /^\+\d+/;
var UNSUPPORTED_FLAG_COUNTRIES = new Set(['AC', 'TA', 'IC']);
var COUNTRY_CONSTRAINTS_CACHE = {};
var COUNTRY_PLACEHOLDER_CACHE = {};
/**
 * Retrieves a placeholder for a given country code.
 * Caches the result to avoid re-computation.
 */
export var getCountryPlaceholder = function (countryCode) {
    if (COUNTRY_PLACEHOLDER_CACHE[countryCode]) {
        return COUNTRY_PLACEHOLDER_CACHE[countryCode];
    }
    try {
        if (getCountries().includes(countryCode)) {
            var exampleNumber = getExampleNumber(countryCode, examples);
            if (exampleNumber) {
                var nationalFormat = exampleNumber.formatNational();
                COUNTRY_PLACEHOLDER_CACHE[countryCode] = nationalFormat;
                return nationalFormat;
            }
        }
    }
    catch (_a) {
        // ignore and fall through to default
    }
    COUNTRY_PLACEHOLDER_CACHE[countryCode] = '123456789';
    return '123456789';
};
/**
 * Retrieves the minimum and maximum length of a phone number for a given country.
 * Caches the result to avoid re-computation.
 */
export var getCountryConstraints = function (countryCode) {
    var _a;
    if (COUNTRY_CONSTRAINTS_CACHE[countryCode]) {
        return COUNTRY_CONSTRAINTS_CACHE[countryCode];
    }
    if (getCountries().includes(countryCode)) {
        try {
            var meta = (_a = libPhoneMetadata.countries) === null || _a === void 0 ? void 0 : _a[countryCode];
            if (meta && Array.isArray(meta[3]) && meta[3].length) {
                var lengths = meta[3];
                var constraints = { minLength: Math.min.apply(Math, lengths), maxLength: Math.max.apply(Math, lengths) };
                COUNTRY_CONSTRAINTS_CACHE[countryCode] = constraints;
                return constraints;
            }
        }
        catch (_b) {
            // ignore and fall through
        }
    }
    var fallback = { minLength: 7, maxLength: 15 };
    COUNTRY_CONSTRAINTS_CACHE[countryCode] = fallback;
    return fallback;
};
export var isPhoneNumberPossible = function (phoneNumber) {
    if (!(phoneNumber === null || phoneNumber === void 0 ? void 0 : phoneNumber.trim()))
        return false;
    try {
        return parsePhoneNumber(phoneNumber).isPossible();
    }
    catch (_a) {
        return false;
    }
};
export var ensurePhoneNumberWithCountryCode = function (phoneNumber, defaultCountry) {
    if (defaultCountry === void 0) { defaultCountry = 'US'; }
    if (!(phoneNumber === null || phoneNumber === void 0 ? void 0 : phoneNumber.trim()))
        return phoneNumber;
    try {
        var parsed = parsePhoneNumber(phoneNumber, defaultCountry);
        return parsed.format('E.164');
    }
    catch (error) {
        var countryCode = String(getCountryCallingCode(defaultCountry));
        var cleanedPhone = phoneNumber.replace(/\D/g, '');
        if (cleanedPhone.startsWith(countryCode)) {
            return "+".concat(cleanedPhone);
        }
        return "+".concat(countryCode).concat(cleanedPhone);
    }
};
export var validatePhoneNumberWithMessage = function (phoneNumber) {
    if (!(phoneNumber === null || phoneNumber === void 0 ? void 0 : phoneNumber.trim()))
        return null;
    if (!isPhoneNumberPossible(phoneNumber)) {
        return ' is not a valid phone number format.';
    }
    return null;
};
export var isValidCountryCode = function (code) {
    return getCountries().includes(code);
};
export var validatePhoneNumberForCountry = function (phoneNumber, countryCode) {
    if (!(phoneNumber === null || phoneNumber === void 0 ? void 0 : phoneNumber.trim()))
        return { isValid: false, isPossible: false };
    try {
        var parsed = parsePhoneNumber(phoneNumber);
        var constraints = getCountryConstraints(countryCode);
        if (parsed.country && parsed.country !== countryCode) {
            return { isValid: false, isPossible: false };
        }
        if (String(parsed.nationalNumber).length > constraints.maxLength) {
            return { isValid: false, isPossible: false };
        }
        return { isValid: parsed.isValid(), isPossible: parsed.isPossible() };
    }
    catch (_a) {
        return { isValid: false, isPossible: false };
    }
};
export var createCountryList = function () {
    var allCountries = getCountries();
    var displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    var filteredCountries = allCountries
        .filter(function (code) { return !UNSUPPORTED_FLAG_COUNTRIES.has(code); })
        .map(function (code) {
        var name = displayNames.of(code) || COUNTRY_NAME_BY_CODE[code] || code;
        return { code: code, name: name };
    });
    var priorityCountries = ['US', 'CA', 'GB'];
    var priority = priorityCountries
        .map(function (code) { return filteredCountries.find(function (country) { return country.code === code; }); })
        .filter(Boolean);
    var remaining = filteredCountries.filter(function (country) { return !priorityCountries.includes(country.code); });
    return __spreadArray(__spreadArray([], priority, true), remaining, true);
};
export var formatPhoneNumber = function (value, countryCode) {
    if (value && isValidCountryCode(countryCode)) {
        return new AsYouType(countryCode).input(value);
    }
    return value;
};
export var cleanPhoneInput = function (value) {
    return value.replace(NON_DIGIT_REGEX, '');
};
/**
 * Parses a full phone number string to extract the national number and country.
 * It handles numbers with or without a country code.
 */
export var parseFullPhoneNumber = function (value, countries, currentCountry) {
    if (!value) {
        return { nationalNumber: '', country: undefined };
    }
    try {
        var parsed = isPhoneNumberPossible(value) ? parsePhoneNumber(value) : undefined;
        if (parsed === null || parsed === void 0 ? void 0 : parsed.country) {
            return {
                nationalNumber: String(parsed.nationalNumber),
                country: parsed.country,
            };
        }
    }
    catch (_a) {
        // ignore and continue
    }
    if (value.startsWith('+')) {
        var matchedCountry = countries.find(function (country) {
            if (isValidCountryCode(country.code)) {
                var countryCode = getCountryCallingCode(country.code);
                return value.startsWith("+".concat(countryCode));
            }
            return false;
        });
        if (matchedCountry) {
            var countryCode = getCountryCallingCode(matchedCountry.code);
            var nationalPart = value.slice("+".concat(countryCode).length);
            return {
                nationalNumber: cleanPhoneInput(nationalPart),
                country: matchedCountry.code,
            };
        }
    }
    return {
        nationalNumber: cleanPhoneInput(value.replace(STARTS_WITH_PLUS_AND_DIGITS_REGEX, '')),
        country: currentCountry,
    };
};
/**
 * Constructs a full international phone number from a national number and a country code.
 */
export var constructInternationalNumber = function (nationalNumber, country) {
    if (!nationalNumber.trim() || !isValidCountryCode(country)) {
        return '';
    }
    var cleanedPhone = cleanPhoneInput(nationalNumber);
    var countryCode = getCountryCallingCode(country);
    return "+".concat(countryCode).concat(cleanedPhone);
};
